import React from "react";
import { Field, FieldArray, Form, Formik, ErrorMessage } from "formik";
import InputMask from "react-input-mask";
import * as Yup from "yup";

const validateCPF = (cpf) => {
	if (!cpf) return false;
	cpf = cpf.replace(/[^\d]/g, "");

	if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
		return false;
	}

	let sum = 0;
	let remainder;

	for (let i = 1; i <= 9; i++) {
		sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
	}
	remainder = (sum * 10) % 11;
	if (remainder === 10 || remainder === 11) remainder = 0;
	if (remainder !== parseInt(cpf.substring(9, 10))) return false;

	sum = 0;
	for (let i = 1; i <= 10; i++) {
		sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
	}
	remainder = (sum * 10) % 11;
	if (remainder === 10 || remainder === 11) remainder = 0;
	if (remainder !== parseInt(cpf.substring(10, 11))) return false;

	return true;
};

// === Defina o Schema de Validação ===
const validationSchema = Yup.object().shape({
	userSinpolDependent: Yup.array().of(
		Yup.object().shape({
			nome: Yup.string()
				.min(2, "Nome inválido")
				.required("Nome é obrigatório"),
			cpf: Yup.string()
				.required("CPF é obrigatório")
				.test("valid-cpf", "CPF inválido", validateCPF),
			dataNascimento: Yup.date().required(
				"Data de nascimento é obrigatória"
			),
			familyRelationship: Yup.string()
				.required("Parentesco é obrigatório")
				.min(3, "Parentesco deve ter pelo menos 3 caracteres"),
		})
	),
});

const DependentInfo = ({ data, updateData, onNextStep, onBackStep }) => {
	const minDate = new Date();
	const day = String(minDate.getDate()).padStart(2, "0");
	const month = String(minDate.getMonth() + 1).padStart(2, "0");
	const minDateFormatted = `${minDate.getFullYear()}-${month}-${day}`;

	return (
		<div>
			<h4>Dependentes</h4>
			<Formik
				initialValues={{
					userSinpolDependent: data.userSinpolDependent || [],
				}}
				validationSchema={validationSchema}
				onSubmit={(values, actions) => {
					try {
						const sanitizedValues = {
							...values,
							userSinpolDependent: values.userSinpolDependent.map(
								(dependent) => ({
									...dependent,
									cpf: dependent.cpf
										? dependent.cpf.replace(/[^\d]/g, "")
										: "",
								})
							),
						};
						updateData(sanitizedValues);
						onNextStep();
						actions.setSubmitting(false);
					} catch (error) {
						console.error("Error during submission:", error);
						actions.setSubmitting(false);
						actions.setStatus("Erro ao enviar o formulário");
					}
				}}
			>
				{({ errors, isSubmitting, submitCount, touched, values }) => (
					<Form>
						{submitCount > 0 && (
							<div className="alert alert-danger mb-3">
								<span className="text-4">
									Verifique os campos abaixo para continuar:
								</span>
								<ul className="mb-0">
									{values.userSinpolDependent.map(
										(_, index) =>
											Object.keys(
												errors?.userSinpolDependent?.[
													index
												] ?? {}
											).map((field) => (
												<li key={`${index}-${field}`}>
													{
														errors
															.userSinpolDependent[
															index
														][field]
													}
												</li>
											))
									)}
								</ul>
							</div>
						)}

						<FieldArray
							name="userSinpolDependent"
							render={(arrayHelpers) => (
								<div>
									{values.userSinpolDependent &&
									values.userSinpolDependent.length > 0 ? (
										values.userSinpolDependent.map(
											(dependent, index) => (
												<div key={index}>
													<div className="row">
														<div className="form-group required d-flex flex-column col-md-6 col-12">
															<label
																htmlFor="nome"
																className="form-label"
															>
																Nome
															</label>
															<Field
																id="nome"
																name={`userSinpolDependent.${index}.nome`}
																type="text"
																className="form-control form-field"
															/>
															{errors
																.userSinpolDependent?.[
																index
															]?.nome &&
																touched
																	.userSinpolDependent?.[
																	index
																]?.nome && (
																	<div className="invalid-feedback d-block">
																		{
																			errors
																				.userSinpolDependent[
																				index
																			]
																				.nome
																		}
																	</div>
																)}
														</div>
														<div className="form-group required d-flex flex-column col-md-6 col-12">
															<label
																htmlFor="dataNascimento"
																className="form-label"
															>
																Data de
																nascimento
															</label>
															<Field
																id="dataNascimento"
																name={`userSinpolDependent.${index}.dataNascimento`}
																type="date"
																max={
																	minDateFormatted
																}
																className="form-control form-field"
															/>
															{errors
																.userSinpolDependent?.[
																index
															]?.dataNascimento &&
																touched
																	.userSinpolDependent?.[
																	index
																]
																	?.dataNascimento && (
																	<div className="invalid-feedback d-block">
																		{
																			errors
																				.userSinpolDependent[
																				index
																			]
																				.dataNascimento
																		}
																	</div>
																)}
														</div>
													</div>

													<div className="row">
														<div className="form-group required d-flex flex-column col-md-6 col-12">
															<label
																htmlFor="cpf"
																className="form-label"
															>
																CPF
															</label>
															<Field
																name={`userSinpolDependent.${index}.cpf`}
															>
																{({
																	field,
																}) => (
																	<InputMask
																		{...field}
																		mask="999.999.999-99"
																		className="form-control form-field"
																		placeholder="000.000.000-00"
																	/>
																)}
															</Field>
															{errors
																.userSinpolDependent?.[
																index
															]?.cpf &&
																touched
																	.userSinpolDependent?.[
																	index
																]?.cpf && (
																	<div className="invalid-feedback d-block">
																		{
																			errors
																				.userSinpolDependent[
																				index
																			]
																				.cpf
																		}
																	</div>
																)}
														</div>
														<div className="form-group required d-flex flex-column col-md-6 col-12">
															<label
																htmlFor="familyRelationship"
																className="form-label"
															>
																Parentesco
															</label>
															<Field
																id="familyRelationship"
																name={`userSinpolDependent.${index}.familyRelationship`}
																type="text"
																className="form-control form-field"
															/>
															{errors
																.userSinpolDependent?.[
																index
															]
																?.familyRelationship &&
																touched
																	.userSinpolDependent?.[
																	index
																]
																	?.familyRelationship && (
																	<div className="invalid-feedback d-block">
																		{
																			errors
																				.userSinpolDependent[
																				index
																			]
																				.familyRelationship
																		}
																	</div>
																)}
														</div>
													</div>

													<div className="d-flex justify-content-end">
														<button
															className="btn btn-outline-primary mx-3"
															type="button"
															onClick={() =>
																arrayHelpers.insert(
																	index,
																	""
																)
															}
														>
															+
														</button>

														<button
															className="btn btn-outline-secondary"
															type="button"
															onClick={() =>
																arrayHelpers.remove(
																	index
																)
															}
														>
															-
														</button>
													</div>
												</div>
											)
										)
									) : (
										<button
											className="btn btn-outline-primary my-4"
											type="button"
											onClick={() =>
												arrayHelpers.push({
													nome: "",
													cpf: "",
													dataNascimento: "",
													familyRelationship: "",
												})
											}
										>
											Adicionar um dependente
										</button>
									)}

									<div className="d-flex justify-content-between mt-3">
										<button
											type="button"
											className="btn btn-secondary"
											onClick={onBackStep}
										>
											Anterior
										</button>
										<button
											type="submit"
											className="btn btn-primary"
											disabled={isSubmitting}
										>
											Próximo
										</button>
									</div>
								</div>
							)}
						/>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default DependentInfo;
